import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { useTranslation } from "react-i18next";
import { useSpDrives, useSpSites } from "~/api/v2/useSp";
import { useState } from "react";
import { updateOnboardingState } from "~/api/v2/requests";
import { toast } from "react-toastify";
import { Button } from "~/atoms/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/atoms/tabs";
import { SearchableItemSelector } from "./SearchableItemSelector";
import { SharePointSiteUrlInput } from "./SharePointSiteUrlInput";

type SiteSelectionStepProps = {
  onSubmit: () => void;
};

export const SelectDestinationStep = ({ onSubmit }: SiteSelectionStepProps) => {
  const { t } = useTranslation();
  const [siteId, setSiteId] = useState<string | null>(null);
  const [driveId, setDriveId] = useState<string | null>(null);
  const [siteSearch, setSiteSearch] = useState("");

  const { data: sites, isLoading: isSitesLoading } = useSpSites({
    search: siteSearch,
  });

  const { data: drives, isLoading: isDrivesLoading } = useSpDrives(siteId);

  const handleSiteSelected = (newSiteId: string) => {
    setSiteId(newSiteId);
    setDriveId(null);
  };

  const handleSubmit = async () => {
    try {
      if (!siteId) return;
      await updateOnboardingState({ siteId, driveId });
      onSubmit();
    } catch (error) {
      console.error("Error updating onboarding state:", error);
      toast.error(t(($) => $.sharepoint.steps.selectDestination.error));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <span>{t(($) => $.sharepoint.steps.selectDestination.header)}</span>
        </CardTitle>
        <CardDescription>
          {t(($) => $.sharepoint.steps.selectDestination.subtitle)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>
              {t(($) => $.sharepoint.steps.selectDestination.site)}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <Tabs defaultValue="search">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="search">
                  {t(($) => $.sharepoint.steps.selectDestination.searchTab)}
                </TabsTrigger>
                <TabsTrigger value="manual">
                  {t(($) => $.sharepoint.steps.selectDestination.manualTab)}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="search" className="space-y-4">
                <SearchableItemSelector
                  value={siteId}
                  onChange={handleSiteSelected}
                  items={sites}
                  isLoading={isSitesLoading}
                  onSearchChange={setSiteSearch}
                  renderItem={(s) => ({
                    primary: s.displayName,
                    secondary: s.webUrl,
                  })}
                />
              </TabsContent>

              <TabsContent value="manual" className="space-y-4">
                <SharePointSiteUrlInput onSiteFound={handleSiteSelected} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              {t(($) => $.sharepoint.steps.selectDestination.drive)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!siteId ? (
              <p className="text-muted-foreground text-sm">
                {t(($) => $.sharepoint.steps.selectDestination.selectSiteFirst)}
              </p>
            ) : (
              <SearchableItemSelector
                value={driveId}
                onChange={setDriveId}
                items={drives}
                isLoading={isDrivesLoading}
                renderItem={(d) => ({ primary: d.name, secondary: d.webUrl })}
              />
            )}
          </CardContent>
        </Card>
      </CardContent>
      <CardFooter>
        <Button disabled={!siteId || !driveId} onClick={handleSubmit}>
          {t(($) => $.commons.buttons.proceed)}
        </Button>
      </CardFooter>
    </Card>
  );
};
